import type { AppealStatus, SuspensionReasonCode } from '../dataProvider/types';
import type {
  PeriodOption,
  ReasonCodeOption,
} from '../actions/JustifiedAction';
import type { SuspensionPhase } from './suspensionsModel';

/** Display + option helpers for the Suspension flow (D-suspension-flow). */

/** Human label per structured reason code. The enum grows additively. */
export const REASON_CODE_LABEL: Record<SuspensionReasonCode, string> = {
  payment_fraud: 'Payment fraud',
  referral_abuse: 'Referral abuse',
  identity_mismatch: 'Identity mismatch',
  chargeback_pattern: 'Chargeback pattern',
  terms_violation: 'Terms violation',
  member_request: 'Member request',
  abusive_conduct: 'Abusive conduct',
};

/**
 * The reason subsets offered to each role (D-support-role). There is ONE reason field
 * (SUSPENSION_REASON_CODES) but the suspend action shows each role the subset that fits
 * its context, so a support suspension does not pollute the fraud reason stats. The
 * fraud subset is the original fraud categories; the support subset is the support-
 * context reasons plus the shared `terms_violation`.
 */
const FRAUD_SUSPENSION_REASONS: readonly SuspensionReasonCode[] = [
  'payment_fraud',
  'referral_abuse',
  'identity_mismatch',
  'chargeback_pattern',
  'terms_violation',
];
const SUPPORT_SUSPENSION_REASONS: readonly SuspensionReasonCode[] = [
  'member_request',
  'abusive_conduct',
  'terms_violation',
];

const toOptions = (
  codes: readonly SuspensionReasonCode[],
): readonly ReasonCodeOption[] =>
  codes.map((code) => ({ value: code, label: REASON_CODE_LABEL[code] }));

/** The FRAUD reason-code select options (the fraud queue and the suspensions worklist).
 *  Unchanged from before the support widening: still the fraud categories only. */
export const REASON_CODE_OPTIONS: readonly ReasonCodeOption[] = toOptions(
  FRAUD_SUSPENSION_REASONS,
);

/** The SUPPORT reason-code select options (D-support-role): the support-context reasons
 *  plus the shared terms_violation, offered when a support agent suspends a member. */
export const SUPPORT_REASON_CODE_OPTIONS: readonly ReasonCodeOption[] =
  toOptions(SUPPORT_SUSPENSION_REASONS);

/**
 * The admin-chosen suspension periods (D-suspension-flow), replacing the old
 * hardcoded 30-day length. The default 30 is listed FIRST so it is the default
 * selection (JustifiedAction defaults the period to the first option).
 */
export const SUSPENSION_PERIOD_OPTIONS: readonly PeriodOption[] = [
  { value: 30, label: '30 days' },
  { value: 7, label: '7 days' },
  { value: 90, label: '90 days' },
];

export const APPEAL_STATUS_LABEL: Record<AppealStatus, string> = {
  none: 'No appeal',
  pending: 'Appeal pending',
  resolved: 'Appeal resolved',
};

/** Chip label per derived phase: a running hold vs one whose period has elapsed. */
export const PHASE_LABEL: Record<SuspensionPhase, string> = {
  running: 'Running',
  elapsed: 'Elapsed, pending lift',
};

export const PHASE_COLOR: Record<SuspensionPhase, 'default' | 'warning'> = {
  running: 'default',
  elapsed: 'warning',
};
